/* =================================================================
   Lahazati media uploader

   The upload boxes on the product page and the guided builder. Storage
   is the Lahazati Uploads app: when its theme embed is on and the store
   is active, the drop zones appear. Until then each box shows a
   cloud-link field instead, so a customer's files can never be silently
   lost.

   Each box writes ONE line item property: the link to its gallery. It is
   written as soon as the first file starts, so adding to cart mid-upload
   still carries the link, and files that finish afterwards appear behind
   it. The hidden input lives in the markup, so switching tiers can
   disable it like any other field.
   ================================================================= */
(function () {
  'use strict';

  var MAX_LABEL = 42;

  function bytes(n) {
    if (n < 1024) return n + ' B';
    if (n < 1048576) return (n / 1024).toFixed(0) + ' KB';
    return (n / 1048576).toFixed(1) + ' MB';
  }

  function shorten(name) {
    if (name.length <= MAX_LABEL) return name;
    var dot = name.lastIndexOf('.');
    var ext = dot > -1 ? name.slice(dot) : '';
    return name.slice(0, MAX_LABEL - ext.length - 1) + '…' + ext;
  }

  /* ---------------- is the app live? ---------------- */

  function appClient() {
    var c = window.LahazatiUploads;
    return c && typeof c.upload === 'function' && c.isConfigured() ? c : null;
  }

  var appReady = new Promise(function (resolve) {
    function tryClient() {
      var client = appClient();
      if (!client) return false;
      client.status().then(function (store) {
        resolve(store && store.active ? { client: client, store: store } : null);
      }, function () { resolve(null); });
      return true;
    }
    if (tryClient()) return;
    // Without the app this simply never resolves, and every box keeps its
    // cloud-link field. No timeout: a late-loading embed still switches on.
    document.addEventListener('lahazati-uploads:loaded', function () {
      if (!tryClient()) resolve(null);
    });
  });

  /* ---------------- engines ---------------- */

  function demoEngine() {
    return {
      maxMb: 0,
      start: function (item, onProgress) {
        return new Promise(function (resolve) {
          var pct = 0;
          var timer = setInterval(function () {
            pct = Math.min(100, pct + 6 + Math.random() * 10);
            onProgress(pct);
            if (pct >= 100) { clearInterval(timer); resolve(); }
          }, 90);
          item.cancel = function () { clearInterval(timer); };
        });
      },
      remove: function (item) { if (item.cancel) item.cancel(); },
      // Deliberately not a URL: a demo run must never look like real files on an order.
      value: function (kept) { return kept ? '(demo only — not stored) ' + kept + (kept === 1 ? ' file' : ' files') : ''; }
    };
  }

  function appEngine(app, label) {
    var group = null;
    var pending = null;

    function ensureGroup() {
      if (group) return Promise.resolve(group);
      if (!pending) {
        pending = app.client.createGroup(label).then(function (created) {
          group = created;
          return created;
        }, function (err) {
          pending = null;
          throw err;
        });
      }
      return pending;
    }

    return {
      maxMb: app.store.maxFileMb || 0,
      start: function (item, onProgress, changed) {
        return ensureGroup().then(function (g) {
          if (item.removed) {
            var stopped = new Error('Cancelled');
            stopped.cancelled = true;
            throw stopped;
          }
          changed();
          item.handle = app.client.upload(item.file, { group: g, onProgress: onProgress });
          return item.handle.promise;
        });
      },
      remove: function (item) { if (item.handle) item.handle.cancel(); },
      value: function (kept) { return group && kept ? group.url : ''; }
    };
  }

  /* ---------------- component ---------------- */

  function setup(root, engine) {
    var input = root.querySelector('[data-upload-input]');
    var drop = root.querySelector('[data-upload-drop]');
    var list = root.querySelector('[data-upload-list]');
    var summary = root.querySelector('[data-upload-summary]');
    var value = root.querySelector('[data-upload-value]');
    if (!input || !drop || !list || !value) return;

    var maxFiles = parseInt(root.dataset.maxFiles, 10) || 40;
    var maxMb = parseInt(root.dataset.maxMb, 10) || 2048;
    if (engine.maxMb) maxMb = Math.min(maxMb, engine.maxMb);
    var items = [];

    function live() {
      return items.filter(function (i) { return !i.removed; });
    }

    // A one-file slot that still shows "Add profile 1" after a file is in it
    // reads as though the upload didn't take.
    function paintCapacity() {
      var full = live().length >= maxFiles;
      drop.hidden = full;
      root.classList.toggle('is-full', full);
    }

    function renderSummary() {
      paintCapacity();
      if (!summary) return;
      var current = live();
      var done = current.filter(function (i) { return i.status === 'done'; }).length;
      var failed = current.filter(function (i) { return i.status === 'error'; }).length;
      var busy = current.filter(function (i) { return i.status === 'uploading'; }).length;

      if (!current.length) {
        summary.textContent = '';
        summary.className = 'lz-upload__summary';
      } else if (busy) {
        summary.className = 'lz-upload__summary';
        summary.textContent = 'Uploading ' + busy + ' of ' + current.length + '… You can add to cart now — files keep uploading in the background.';
      } else if (failed) {
        summary.className = 'lz-upload__summary lz-upload__summary--warn';
        summary.textContent = done + ' ready, ' + failed + " didn't upload — tap retry, or send them later.";
      } else {
        summary.className = 'lz-upload__summary lz-upload__summary--done';
        summary.textContent = done === 1 ? '1 file ready' : done + ' files ready';
      }
    }

    function sync() {
      var kept = live().filter(function (i) { return i.status === 'uploading' || i.status === 'done'; }).length;
      var next = engine.value(kept);
      if (value.value !== next) {
        value.value = next;
        // Lets the tier summary ("N of M details added") refresh.
        value.dispatchEvent(new Event('input', { bubbles: true }));
      }
      renderSummary();
      root.dispatchEvent(new CustomEvent('lahazati:upload-change', {
        bubbles: true,
        detail: { files: kept, total: live().length }
      }));
    }

    function makeThumb(item) {
      var wrap = document.createElement('div');
      wrap.className = 'lz-upload__thumb';
      var type = item.file.type || '';
      if (type.indexOf('video/') === 0) {
        var v = document.createElement('video');
        v.src = item.preview;
        v.muted = true;
        v.playsInline = true;
        v.preload = 'metadata';
        wrap.appendChild(v);
        var badge = document.createElement('span');
        badge.className = 'lz-upload__badge';
        badge.textContent = 'video';
        wrap.appendChild(badge);
      } else if (type.indexOf('image/') === 0) {
        var img = document.createElement('img');
        img.src = item.preview;
        img.alt = '';
        wrap.appendChild(img);
      } else {
        wrap.classList.add('lz-upload__thumb--file');
        wrap.textContent = (item.file.name.split('.').pop() || '?').toUpperCase();
      }
      return wrap;
    }

    function paint(item) {
      var el = item.el;
      el.row.classList.toggle('is-done', item.status === 'done');
      el.row.classList.toggle('is-error', item.status === 'error');
      if (item.status === 'done') {
        el.fill.style.width = '100%';
        el.note.textContent = 'Ready';
      } else if (item.status === 'error') {
        el.note.textContent = item.error;
      }
      el.retry.hidden = !(item.status === 'error' && item.retriable);
    }

    function removeItem(item) {
      item.removed = true;
      engine.remove(item);
      if (item.preview) URL.revokeObjectURL(item.preview);
      item.el.row.remove();
      sync();
    }

    function start(item) {
      item.status = 'uploading';
      item.error = '';
      item.el.fill.style.width = '0%';
      item.el.note.textContent = 'Uploading…';
      paint(item);

      engine.start(item, function (pct) {
        if (item.removed || item.status !== 'uploading') return;
        item.el.fill.style.width = pct + '%';
        item.el.note.textContent = 'Uploading ' + Math.round(pct) + '%';
      }, sync).then(function () {
        if (item.removed) return;
        item.status = 'done';
        paint(item);
        sync();
      }).catch(function (err) {
        if (item.removed || (err && err.cancelled)) return;
        item.status = 'error';
        item.error = (err && err.message) || 'Upload failed';
        // Refusals (wrong type, too large) won't fix themselves on retry.
        item.retriable = !(err && err.status && err.status < 500 && err.status !== 429);
        paint(item);
        sync();
      });
      sync();
    }

    function renderItem(item) {
      var row = document.createElement('div');
      row.className = 'lz-upload__item';
      row.appendChild(makeThumb(item));

      var meta = document.createElement('div');
      meta.className = 'lz-upload__meta';
      var name = document.createElement('span');
      name.className = 'lz-upload__name';
      name.textContent = shorten(item.file.name);
      meta.appendChild(name);
      var bar = document.createElement('div');
      bar.className = 'lz-upload__bar';
      var fill = document.createElement('div');
      fill.className = 'lz-upload__bar-fill';
      bar.appendChild(fill);
      meta.appendChild(bar);
      var note = document.createElement('span');
      note.className = 'lz-upload__note';
      note.textContent = bytes(item.file.size);
      meta.appendChild(note);
      row.appendChild(meta);

      var actions = document.createElement('div');
      actions.className = 'lz-upload__actions';
      var retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'lz-upload__action';
      retry.innerHTML = '&#8635;';
      retry.setAttribute('aria-label', 'Retry ' + item.file.name);
      retry.hidden = true;
      retry.addEventListener('click', function () { start(item); });
      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'lz-upload__action';
      remove.innerHTML = '&times;';
      remove.setAttribute('aria-label', 'Remove ' + item.file.name);
      remove.addEventListener('click', function () { removeItem(item); });
      actions.appendChild(retry);
      actions.appendChild(remove);
      row.appendChild(actions);

      item.el = { row: row, fill: fill, note: note, retry: retry };
      list.appendChild(row);
    }

    function add(files) {
      Array.prototype.forEach.call(files, function (file) {
        if (live().length >= maxFiles) return;
        var item = { file: file, status: 'queued', preview: URL.createObjectURL(file) };
        items.push(item);
        renderItem(item);
        if (file.size > maxMb * 1048576) {
          item.status = 'error';
          item.error = 'Too large (max ' + maxMb + ' MB)';
          item.retriable = false;
          paint(item);
          renderSummary();
          return;
        }
        start(item);
      });
    }

    input.addEventListener('change', function () {
      add(this.files);
      this.value = '';
    });

    drop.addEventListener('click', function () { input.click(); });
    drop.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.click(); }
    });
    ['dragenter', 'dragover'].forEach(function (type) {
      drop.addEventListener(type, function (e) {
        e.preventDefault();
        e.stopPropagation();
        drop.classList.add('is-dragging');
      });
    });
    ['dragleave', 'drop'].forEach(function (type) {
      drop.addEventListener(type, function (e) {
        e.preventDefault();
        e.stopPropagation();
        drop.classList.remove('is-dragging');
      });
    });
    drop.addEventListener('drop', function (e) {
      if (e.dataTransfer && e.dataTransfer.files) add(e.dataTransfer.files);
    });
  }

  function build(root) {
    var mode = root.getAttribute('data-upload-mode') || 'links';
    if (mode === 'demo') {
      root.hidden = false;
      setup(root, demoEngine());
      return;
    }

    appReady.then(function (app) {
      if (!app) return; // keep the cloud-link field
      var slot = root.closest('[data-upload-slot]');
      var fallback = slot ? slot.querySelector('[data-upload-fallback]') : null;
      root.hidden = false;
      if (fallback && !fallback.value) fallback.hidden = true;
      setup(root, appEngine(app, root.getAttribute('data-prop-prefix') || 'Files'));
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-uploader]').forEach(function (el) {
      if (el.dataset.uploaderReady) return;
      el.dataset.uploaderReady = '1';
      build(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  window.LahazatiUploader = { init: init };
})();
